import React from 'react';
import { Truck, Clock, AlertTriangle } from 'lucide-react';

// Shown at checkout when a basket contains an item that is out of stock /
// backordered. The customer picks between holding the whole order until
// everything is ready (one postage) or taking the in-stock items now and
// paying a second postage for the delayed items. The second postage is
// collected with the order — the warehouse never has to chase it later.
export default function SplitDeliveryChoice({ unavailableNames = [], firstPostage = 0, secondPostage = 0, value, onChange }) {
  const options = [
    {
      key: 'together',
      icon: Clock,
      title: 'SHIP TOGETHER WHEN ALL ITEMS ARE READY',
      description: 'We hold your whole order until the out-of-stock item comes back in, then send everything in one parcel.',
      postage: firstPostage,
      postageLabel: 'one postage charge',
    },
    {
      key: 'split',
      icon: Truck,
      title: 'SHIP AVAILABLE ITEMS NOW',
      description: 'We post the in-stock items straight away and send the delayed item separately once it arrives.',
      postage: firstPostage + secondPostage,
      postageLabel: 'two postage charges',
    },
  ];

  return (
    <div className="bg-white/[0.02] border border-white/5 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-7 h-7 bg-primary/10 border border-primary/20 flex items-center justify-center">
          <AlertTriangle className="w-3.5 h-3.5 text-primary" />
        </div>
        <p className="text-white/70 text-[10px] font-bold tracking-[0.15em]">DELIVERY OPTIONS</p>
        <div className="flex-1 h-px bg-white/5" />
      </div>

      <p className="text-white/50 text-xs leading-relaxed mb-4">
        {unavailableNames.length === 1
          ? <><span className="text-white font-medium">{unavailableNames[0]}</span> is currently out of stock.</>
          : <>These items are currently out of stock: <span className="text-white font-medium">{unavailableNames.join(', ')}</span>.</>}
        {' '}Choose how you'd like your order delivered.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`flex items-start gap-3 p-4 border text-left transition-all ${active ? 'border-primary/50 bg-primary/5' : 'border-white/10 hover:border-white/25'}`}
            >
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? 'text-primary' : 'text-white/40'}`} />
              <div className="min-w-0">
                <p className={`text-[11px] font-bold tracking-wider leading-snug ${active ? 'text-white' : 'text-white/60'}`}>{opt.title}</p>
                <p className="text-white/40 text-[10px] mt-1.5 leading-relaxed">{opt.description}</p>
                <p className={`text-[10px] font-bold tracking-wider mt-2 ${active ? 'text-primary' : 'text-white/40'}`}>
                  {opt.postage > 0 ? `£${opt.postage.toFixed(2)} postage · ${opt.postageLabel}` : 'No postage charge'}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {secondPostage > 0 && (
        <p className="text-white/30 text-[10px] mt-3 leading-relaxed">
          The extra £{secondPostage.toFixed(2)} postage for the delayed item is taken with this payment, so nothing more is owed later.
        </p>
      )}
    </div>
  );
}